import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format, addDays, addYears } from 'date-fns';
import { ArrowLeft, X, AlertTriangle } from 'lucide-react';
import { customerService as customerSvc } from '../api/services/customerService';
import { licenseService as licenseSvc } from '../api/services/licenseService';
import { moduleService as moduleSvc } from '../api/services/moduleService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { CustomerModal } from '../components/customers/CustomerModal';
import {
  Button,
  Card,
  PageHeader,
} from '@license-management/ui';
import ModuleGrid from '../components/modules/ModuleGrid';
import { CreateCustomerInput } from '../types/api';
import ErrorBoundary from '../components/common/ErrorBoundary';

interface LicenseFormData {
  customerId: string;
  type: 'trial' | 'enterprise';
  validFrom: string;
  validUntil: string;
  maxActivations: number;
  modules: string[];
}

const NewLicense: React.FC = () => {
  console.log('[NewLicense] Component mounting');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showNoCustomersWarning, setShowNoCustomersWarning] = useState(true);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

  useEffect(() => {
    console.log('[NewLicense] Component mounted');
    return () => {
      console.log('[NewLicense] Component unmounting');
    };
  }, []);

  useEffect(() => {
    console.log('[NewLicense] Customer modal state changed:', isCustomerModalOpen);
  }, [isCustomerModalOpen]);

  const { register, handleSubmit, watch, setValue } = useForm<LicenseFormData>({
    defaultValues: {
      type: 'trial',
      validFrom: format(new Date(), 'yyyy-MM-dd'),
      validUntil: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      maxActivations: 1,
      modules: []
    }
  });

  const { data: customers = [], isLoading: isLoadingCustomers } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerSvc.getCustomers()
  });

  const { data: modules = [], isLoading: isLoadingModules } = useQuery({
    queryKey: ['modules'],
    queryFn: () => moduleSvc.getModules()
  });

  const createCustomerMutation = useMutation({
    mutationFn: async (data: CreateCustomerInput) => {
      console.log('[NewLicense] Creating customer with data:', data);
      try {
        const result = await customerSvc.createCustomer(data);
        console.log('[NewLicense] Customer created successfully:', result);
        return result;
      } catch (error) {
        console.error('[NewLicense] Error creating customer:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('[NewLicense] Customer creation mutation succeeded');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setIsCustomerModalOpen(false);
    },
    onError: (error) => {
      console.error('[NewLicense] Customer creation mutation failed:', error);
    }
  });

  const createLicenseMutation = useMutation({
    mutationFn: (data: LicenseFormData) => licenseSvc.createLicense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      navigate('/licenses');
    }
  });

  const licenseType = watch('type');
  React.useEffect(() => {
    const today = new Date();
    setValue(
      'validUntil',
      format(
        licenseType === 'trial' ? addDays(today, 30) : addYears(today, 1),
        'yyyy-MM-dd'
      )
    );
  }, [licenseType, setValue]);

  const selectedModules = watch('modules');
  const handleModuleToggle = (moduleId: string) => {
    const newModules = selectedModules.includes(moduleId)
      ? selectedModules.filter(id => id !== moduleId)
      : [...selectedModules, moduleId];
    setValue('modules', newModules);
  };

  if (isLoadingCustomers || isLoadingModules) return <LoadingSpinner />;
  
  return (
    <>
      <CustomerModal 
        isOpen={isCustomerModalOpen}
        onClose={() => {
          console.log('[NewLicense] Closing customer modal');
          setIsCustomerModalOpen(false);
        }}
        onSubmit={async (data) => {
          console.log('[NewLicense] Customer form submitted with data:', data);
          await createCustomerMutation.mutateAsync(data as CreateCustomerInput);
        }}
        isSubmitting={createCustomerMutation.isPending}
      />
      
      {customers.length === 0 && showNoCustomersWarning ? (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4">
            <div className="fixed inset-0 bg-zinc-900/75 transition-opacity" onClick={() => setShowNoCustomersWarning(false)}></div>
            <Card className="relative w-full max-w-lg p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-zinc-400 hover:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  onClick={() => setShowNoCustomersWarning(false)}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="text-center">
                <AlertTriangle className="mx-auto h-12 w-12 text-amber-500 mb-4" />
                <h2 className="text-lg font-medium text-zinc-900 mb-4">No Customers Found</h2>
                <p className="text-zinc-500 mb-4">
                  You need to create at least one customer before you can create a license.
                </p>
                <Button
                  onClick={() => setIsCustomerModalOpen(true)}
                  loading={createCustomerMutation.isPending}
                >
                  Create Customer
                </Button>
              </div>
            </Card>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <PageHeader
            title="Create New License"
            description="Fill out the form below to create a new license for a customer."
            actions={
              <Button
                variant="secondary"
                onClick={() => navigate('/licenses')}
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to Licenses
              </Button>
            }
          />

          <Card className="max-w-4xl">
            <form onSubmit={handleSubmit(data => createLicenseMutation.mutate(data))} className="space-y-8 p-6">
              <div className="space-y-8 divide-y divide-zinc-200">

                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-4">
                    <label htmlFor="customer" className="block text-sm font-medium text-zinc-700">
                      Customer
                    </label>
                    <div className="mt-1">
                      <select
                        id="customer"
                        {...register('customerId')}
                        className="mt-1 block w-full rounded-md border-zinc-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      >
                        <option value="">Select a customer</option>
                        {customers.map((customer) => (
                          <option key={customer.customerId} value={customer.customerId}>
                            {customer.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="type" className="block text-sm font-medium text-zinc-700">
                      License Type
                    </label>
                    <div className="mt-1">
                      <select
                        id="type"
                        {...register('type')}
                        className="mt-1 block w-full rounded-md border-zinc-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      >
                        <option value="trial">Trial</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="validFrom" className="block text-sm font-medium text-zinc-700">
                      Valid From
                    </label>
                    <div className="mt-1">
                      <input
                        type="date"
                        id="validFrom"
                        {...register('validFrom')}
                        className="mt-1 block w-full rounded-md border-zinc-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="validUntil" className="block text-sm font-medium text-zinc-700">
                      Valid Until
                    </label>
                    <div className="mt-1">
                      <input
                        type="date"
                        id="validUntil"
                        {...register('validUntil')}
                        className="mt-1 block w-full rounded-md border-zinc-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <h3 className="text-lg font-medium text-zinc-900 mb-4">Modules</h3>
                  <ModuleGrid
                    modules={modules}
                    selectedModules={selectedModules}
                    onModuleToggle={handleModuleToggle}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-zinc-200">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/licenses')}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createLicenseMutation.isPending}
                >
                  Create License
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </>
  );
};

export default function NewLicenseWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <NewLicense />
    </ErrorBoundary>
  );
}
